import { Anchor, Flex, Group, Stack, Text, ThemeIcon, useMantineColorScheme } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuctionDescriptionHeader } from "./components/AuctionDescriptionHeader";
import {
  getRenderCondition,
  getRenderDescription,
  getRenderEstimate,
  getRenderListingPrice,
  getRenderPrice,
} from "./functions/auction-description";
import { getColumns1, getColumns2, getColumns3 } from "./functions/columns";

import { LabelValue, TransactionStatusBadge } from "@/components/elements";
import { IconLink } from "@/components/icons";
import { defaultCurrency } from "@/lib/currency";
import { useAuctionCars } from "@/services/auctions/get-auction-cars";
import { useMarketCar } from "@/services/market/get-market-car";
import { getTransactionNotAvailableLabel } from "@/utils/transactionRecord";

import type { LabelValueProps } from "@/components/elements";
import type { AuctionCarTransaction } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { FC, ReactNode } from "react";

export const DESCRIPTION_MAX_LENGTH = 500;

type AuctionDescriptionProps = {
  auctionId?: string;
  currentTransaction?: AuctionCarTransaction;
  marketCarId?: string;
};

export const AuctionDescription: FC<AuctionDescriptionProps> = ({
  auctionId,
  currentTransaction,
  marketCarId,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "#c9c9c9" : "#222222";
  const green = isDark ? "#00ed62" : "#1C8028";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const effectiveAuctionId = auctionId ?? currentTransaction?.auction_id;

  const { data: marketCar } = useMarketCar({ carId: marketCarId });
  const { data: auctionCars } = useAuctionCars({
    auctionId: effectiveAuctionId,
    page: 1,
    pageSize: 999999,
  });

  const auctionCar = useMemo(() => {
    if (!auctionCars?.results?.length || !currentTransaction) {
      return undefined;
    }

    return auctionCars.results.find(
      (car) =>
        car.vin === currentTransaction.vin ||
        (marketCarId ? car.taxonomy_id === marketCarId : false)
    );
  }, [auctionCars, currentTransaction, marketCarId]);

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [currentTransaction?.vin, currentTransaction?.date, currentTransaction?.id]);

  if (!currentTransaction) {
    return null;
  }

  const makeName = auctionCar?.make_name ?? marketCar?.make_name;
  const carName = auctionCar
    ? `${auctionCar.make_name} ${auctionCar.model_name}`
    : marketCar
      ? `${marketCar.make_name} ${marketCar.model_name}`
      : undefined;

  const notAvailable = getTransactionNotAvailableLabel(t);
  const title = [currentTransaction.production_year, carName].filter(Boolean).join(" ");

  /* Functions */
  const renderPrice = getRenderPrice(currentTransaction, notAvailable, currency);
  const renderListingPrice = getRenderListingPrice(t, currentTransaction, renderPrice);
  const renderEstimate = getRenderEstimate(auctionCar, currency, renderListingPrice);
  const renderCondition = getRenderCondition(t, isDark);

  const column1: LabelValueProps[] = getColumns1(
    t,
    renderEstimate,
    renderPrice,
    currentTransaction
  );

  const column2: LabelValueProps[] = getColumns2(
    t,
    currentTransaction,
    notAvailable,
    green,
    marketCar
  );

  const column3: LabelValueProps[] = getColumns3(
    t,
    renderCondition,
    currentTransaction,
    renderPrice
  );

  function renderColumn(items: LabelValueProps[]): ReactNode {
    return (
      <Stack flex={1} gap="md">
        {items.map((item, index) => (
          <LabelValue key={index} {...item} c={isDark ? "gray" : "dark"} />
        ))}
      </Stack>
    );
  }

  const renderDescription = getRenderDescription(
    descriptionExpanded,
    textColor,
    setDescriptionExpanded,
    t
  );

  return (
    <Stack gap="lg">
      {/* Images */}
      {currentTransaction.more.image && (
        <AuctionDescriptionHeader
          currentTransaction={currentTransaction}
          makeName={makeName}
          title={title}
        />
      )}

      {/* Title */}
      {(currentTransaction.status || title) && (
        <Group gap="md" align="center" wrap="wrap">
          {currentTransaction.result && (
            <TransactionStatusBadge status={currentTransaction.result} />
          )}
          {title && (
            <Text fw={500} fz="lg">
              {title}
            </Text>
          )}
        </Group>
      )}

      {/* Columns */}
      <Flex
        direction={{ base: "row" }}
        wrap="wrap"
        gap={{ base: "md", sm: "xl" }}
        align="flex-start"
        w="100%"
      >
        {renderColumn(column1)}
        {renderColumn(column2)}
        {renderColumn(column3)}
      </Flex>

      {/* Description */}
      {currentTransaction.more.description &&
        renderDescription(currentTransaction.more.description)}

      {/* Anchor */}
      {currentTransaction.more.website && (
        <Anchor
          href={currentTransaction.more.website}
          target="_blank"
          rel="noopener noreferrer"
          tt="uppercase"
          fw={500}
        >
          <Group gap="md">
            {t("components.AuctionDescription.visit_website")}
            <ThemeIcon variant="light" size="md">
              <IconLink />
            </ThemeIcon>
          </Group>
        </Anchor>
      )}
    </Stack>
  );
};
